import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';

interface BibSearchBarProps {
  onSearch: (bib: string) => void;
  loading?: boolean;
}

export function BibSearchBar({ onSearch, loading = false }: BibSearchBarProps) {
  const [bib, setBib] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();
  const gradient = theme.custom?.gradient || 'linear-gradient(135deg, #FF8800 0%, #6A2BFF 100%)';

  const toHalfWidth = (str: string): string => {
    return str.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 65248));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = toHalfWidth(e.target.value).replace(/[^0-9]/g, '');
    setBib(value);
  };

  const handleSearch = () => {
    if (bib.trim()) {
      onSearch(bib.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 420,
        mx: 'auto',
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
      }}
    >
      {/* 入力フィールド - グラデーションボーダー */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          borderRadius: '12px',
          padding: '2px',
          height: 48,
          background: isFocused ? gradient : theme.palette.grey[300],
          transition: 'background 0.2s ease, box-shadow 0.2s ease',
          boxShadow: isFocused ? '0 0 0 3px rgba(255, 136, 0, 0.15)' : 'none',
        }}
      >
        <InputBase
          value={bib}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="ゼッケン番号"
          disabled={loading}
          inputProps={{
            inputMode: 'numeric',
          }}
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: theme.palette.background.paper,
            borderRadius: '10px',
            px: 2,
            fontSize: '1rem',
            fontWeight: 500,
            letterSpacing: '0.05em',
            '& input': {
              textAlign: 'center',
              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
            },
          }}
        />
      </Box>

      {/* 検索ボタン */}
      <Button
        onClick={handleSearch}
        disabled={!bib.trim() || loading}
        variant="contained"
        sx={{
          minWidth: 56,
          height: 48,
          borderRadius: '12px',
          background: gradient,
          boxShadow: '0 4px 14px rgba(255, 136, 0, 0.35)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            background: gradient,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(255, 136, 0, 0.45)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            background: theme.palette.grey[300],
            boxShadow: 'none',
          },
        }}
      >
        <SearchIcon sx={{ fontSize: 24 }} />
      </Button>
    </Box>
  );
}
